import React, { useState, useEffect, useMemo } from 'react';
import { Download, Loader2, Filter, Users, Package } from 'lucide-react';
import { salesReportsAPI, branchAPI, customerAPI } from '../../services/api';
import DateRangeFilter from './DateRangeFilter';
import { toast } from 'sonner';

const PartyProductSalesReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedSalesman, setSelectedSalesman] = useState('');
  const [branches, setBranches] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showColumns, setShowColumns] = useState({
    customer_name: true,
    salesman: true,
  });
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'pivot'

  useEffect(() => {
    Promise.all([
      branchAPI.getAll().catch(() => []),
      customerAPI.getAll().catch(() => []),
    ]).then(([b, c]) => {
      setBranches(b);
      setCustomers(c);
    });
  }, []);

  useEffect(() => {
    fetchData();
  }, [dateRange, selectedCustomer, selectedBranch, selectedSalesman]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateRange.from) params.from_date = dateRange.from;
      if (dateRange.to) params.to_date = dateRange.to;
      if (selectedCustomer) params.customer = selectedCustomer;
      if (selectedBranch) params.branch = selectedBranch;
      if (selectedSalesman) params.salesman = selectedSalesman;
      
      const result = await salesReportsAPI.getPartyProductSales(params);
      setData(result);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = {
        columns: Object.entries(showColumns).filter(([, v]) => v).map(([k]) => k).join(','),
      };
      if (dateRange.from) params.from_date = dateRange.from;
      if (dateRange.to) params.to_date = dateRange.to;
      if (selectedCustomer) params.customer = selectedCustomer;
      if (selectedBranch) params.branch = selectedBranch;
      if (selectedSalesman) params.salesman = selectedSalesman;
      
      const blob = await salesReportsAPI.exportPartyProductSales(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `party_product_sales_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Report exported');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Limit displayed products to top 20 for table view
  const displayProducts = useMemo(() => {
    if (!data?.products) return [];
    return data.products.slice(0, 20);
  }, [data?.products]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#24549c' }} />
      </div>
    );
  }

  return (
    <div data-testid="party-product-sales-report">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: 'DM Sans' }}>
            <Users className="w-5 h-5" style={{ color: '#24549c' }} />
            Party-wise Product Sales
          </h2>
          <p className="text-xs text-gray-500">Customer-wise product sales breakdown</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 text-[10px] font-semibold rounded-md transition-colors ${
                viewMode === 'table' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'
              }`}
            >
              Table View
            </button>
            <button
              onClick={() => setViewMode('pivot')}
              className={`px-3 py-1.5 text-[10px] font-semibold rounded-md transition-colors ${
                viewMode === 'pivot' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'
              }`}
            >
              Pivot View
            </button>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting || !data?.items?.length}
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-50"
            style={{ backgroundColor: '#24549c' }}
            data-testid="export-party-sales-btn"
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Export Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-50 rounded-xl p-4 mb-5">
        <div className="flex flex-wrap items-center gap-4">
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
          
          <div>
            <label className="text-[10px] text-gray-500 font-semibold uppercase mb-1 block">Customer</label>
            <select
              value={selectedCustomer}
              onChange={e => setSelectedCustomer(e.target.value)}
              className="px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white min-w-[180px]"
              data-testid="customer-filter"
            >
              <option value="">All Customers</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="text-[10px] text-gray-500 font-semibold uppercase mb-1 block">Branch</label>
            <select
              value={selectedBranch}
              onChange={e => setSelectedBranch(e.target.value)}
              className="px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white min-w-[150px]"
              data-testid="branch-filter"
            >
              <option value="">All Branches</option>
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="text-[10px] text-gray-500 font-semibold uppercase mb-1 block">Salesman</label>
            <select
              value={selectedSalesman}
              onChange={e => setSelectedSalesman(e.target.value)}
              className="px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white min-w-[150px]"
              data-testid="salesman-filter"
            >
              <option value="">All Salesmen</option>
              {(data?.filters?.salesmen || []).map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Column Selector */}
          <div className="relative group">
            <label className="text-[10px] text-gray-500 font-semibold uppercase mb-1 block">Columns</label>
            <button className="flex items-center gap-1.5 px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white hover:bg-gray-50">
              <Filter className="w-3.5 h-3.5" /> Select Columns
            </button>
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 hidden group-hover:block z-10 min-w-[180px]">
              {Object.entries({
                customer_name: 'Customer Name',
                salesman: 'Salesman',
              }).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 py-1 text-xs cursor-pointer hover:bg-gray-50 px-1 rounded">
                  <input
                    type="checkbox"
                    checked={showColumns[key]}
                    onChange={e => setShowColumns(prev => ({ ...prev, [key]: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {[
          { label: 'Total Customers', value: data?.summary?.total_customers || 0 },
          { label: 'Products Sold', value: data?.summary?.total_products || 0 },
          { label: 'Total Qty', value: (data?.summary?.grand_total_qty || 0).toLocaleString('en-IN') },
          { label: 'Total Value', value: `₹${(data?.summary?.grand_total_value || 0).toLocaleString('en-IN')}` },
        ].map((kpi, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-[10px] text-gray-500 font-semibold uppercase">{kpi.label}</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* Data Table */}
      {viewMode === 'table' ? (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase sticky left-0 bg-gray-50">#</th>
                  {showColumns.customer_name && <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase min-w-[200px]">Customer</th>}
                  {showColumns.salesman && <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase">Salesman</th>}
                  {displayProducts.map(p => (
                    <th key={p.id} className="px-3 py-3 text-center text-[10px] font-semibold text-gray-600 uppercase min-w-[100px]" colSpan={2}>
                      <span title={p.name}>{p.name.substring(0, 15)}{p.name.length > 15 ? '...' : ''}</span>
                    </th>
                  ))}
                  <th className="px-3 py-3 text-center text-[10px] font-semibold text-gray-600 uppercase bg-blue-50" colSpan={2}>Total</th>
                </tr>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th></th>
                  {showColumns.customer_name && <th></th>}
                  {showColumns.salesman && <th></th>}
                  {displayProducts.map(p => (
                    <React.Fragment key={p.id}>
                      <th className="px-2 py-1 text-[9px] text-gray-500">Qty</th>
                      <th className="px-2 py-1 text-[9px] text-gray-500">Val</th>
                    </React.Fragment>
                  ))}
                  <th className="px-2 py-1 text-[9px] text-gray-500 bg-blue-50">Qty</th>
                  <th className="px-2 py-1 text-[9px] text-gray-500 bg-blue-50">Value</th>
                </tr>
              </thead>
              <tbody>
                {(data?.items || []).map((item, idx) => (
                  <tr key={item.customer_id || idx} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-400 sticky left-0 bg-white">{idx + 1}</td>
                    {showColumns.customer_name && <td className="px-3 py-2 font-medium">{item.customer_name}</td>}
                    {showColumns.salesman && <td className="px-3 py-2 text-gray-500">{item.salesman || '-'}</td>}
                    {displayProducts.map(p => {
                      const pd = item.products?.[p.id] || {};
                      return (
                        <React.Fragment key={p.id}>
                          <td className="px-2 py-2 text-right">{pd.qty || '-'}</td>
                          <td className="px-2 py-2 text-right text-gray-600">{pd.value ? `₹${(pd.value/1000).toFixed(0)}K` : '-'}</td>
                        </React.Fragment>
                      );
                    })}
                    <td className="px-2 py-2 text-right font-semibold bg-blue-50">{item.total_qty}</td>
                    <td className="px-2 py-2 text-right font-semibold bg-blue-50">₹{item.total_value?.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
                {(!data?.items || data.items.length === 0) && (
                  <tr>
                    <td colSpan={20} className="px-3 py-10 text-center text-gray-400">
                      <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      No sales data found for the selected filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {data?.products?.length > 20 && (
            <div className="px-4 py-2 text-xs text-amber-600 bg-amber-50 border-t border-amber-100">
              Showing top 20 products. Export to Excel to see all {data.products.length} products.
            </div>
          )}
        </div>
      ) : (
        /* Pivot View - Customer as rows, Products summary */
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase">#</th>
                  <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase min-w-[200px]">Customer</th>
                  <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase">Salesman</th>
                  <th className="px-3 py-3 text-center text-[10px] font-semibold text-gray-600 uppercase">Products</th>
                  <th className="px-3 py-3 text-center text-[10px] font-semibold text-gray-600 uppercase">Dispatches</th>
                  <th className="px-3 py-3 text-right text-[10px] font-semibold text-gray-600 uppercase">Total Qty</th>
                  <th className="px-3 py-3 text-right text-[10px] font-semibold text-gray-600 uppercase">Total Value</th>
                </tr>
              </thead>
              <tbody>
                {(data?.items || []).map((item, idx) => (
                  <tr key={item.customer_id || idx} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                    <td className="px-3 py-2 font-medium">{item.customer_name}</td>
                    <td className="px-3 py-2 text-gray-500">{item.salesman || '-'}</td>
                    <td className="px-3 py-2 text-center">{Object.keys(item.products || {}).length}</td>
                    <td className="px-3 py-2 text-center">{item.total_dispatches}</td>
                    <td className="px-3 py-2 text-right font-semibold">{item.total_qty}</td>
                    <td className="px-3 py-2 text-right font-semibold">₹{item.total_value?.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartyProductSalesReport;
